import { useCallback, useEffect, useMemo, useState } from "react";
import MacroAdjustmentStepThreeBasicTable from "./MacroAdjustmentStepThreeBasicTable";
import {
  CategoryDlzShare,
  CategoryPackMix,
  EnergyTypeEmissionFactor,
  IngredientEmissionFactor,
  PartMaterialEmissionFactor,
  VehicleMixEmissionFactor,
} from "orval/generated/models";
import { useFormikContext } from "formik";
import {
  AccordionArrayAggredatedDataType,
  BasicTableAgreggatedDataType,
  CountryAccordionAggredatedDataType,
  MacroAdjustmentCombinations,
  MacroAdjustmentTableAccordion,
  MacroAdjustmentWizardFormikValues,
} from "../MacroAdjustmentTypes";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid2 as Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { deepClone } from "utils";
import { CustomIcon, Icons } from "components/common";
import {
  convertIsocodeToCountry,
  useCountries,
} from "utils/CountriesProvider/CountriesProvider";
import MacroAdjustmentStepThreeCompositeTable from "./MacroAdjustmentStepThreeCompositeTable";
import { useGetCategoriesCategoriesGet } from "orval/generated/endpoint";

export type MacroAdjustmentStepThreeAccordionArrayProps = {
  aggregatedData: AccordionArrayAggredatedDataType;
  adjustmentCombination: MacroAdjustmentCombinations;
  baseYear: number;
  targetYear: number;
  validationErrors: { [key: string]: string | undefined };
  setValidationErrors: (
    errors: (prev: { [key: string]: string | undefined }) => {
      [key: string]: string | undefined;
    },
  ) => void;
};

function MacroAdjustmentStepThreeAccordionArray({
  aggregatedData,
  adjustmentCombination,
  targetYear,
  baseYear,
  validationErrors,
  setValidationErrors,
}: MacroAdjustmentStepThreeAccordionArrayProps) {
  const { palette } = useTheme();
  const { values } = useFormikContext<MacroAdjustmentWizardFormikValues>();
  const { t } = useTranslation(["macro"]);
  const { countries } = useCountries();
  const { data: categories } = useGetCategoriesCategoriesGet({
    query: {
      enabled: [
        MacroAdjustmentCombinations.SalesMix_CategoryMix,
        MacroAdjustmentCombinations.SalesMix_PackMix,
        MacroAdjustmentCombinations.SalesMix_DLZShare,
      ].includes(adjustmentCombination),
    },
  });

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(true);

  const getAccordionName = useCallback(
    (accordion: MacroAdjustmentTableAccordion) => {
      switch (adjustmentCombination) {
        case MacroAdjustmentCombinations.Packaging_PackagingEmissionFactors:
          return values.selectedEntities.find(
            (entity) =>
              // eslint-disable-next-line eqeqeq
              entity.id ==
              (accordion as PartMaterialEmissionFactor).part_material_id,
          )?.value;
        case MacroAdjustmentCombinations.Ingredients_IngredientsEmissionFactors:
          return values.selectedEntities.find(
            (entity) =>
              // eslint-disable-next-line eqeqeq
              entity.id ==
              (accordion as IngredientEmissionFactor).ingredient_id,
          )?.value;
        case MacroAdjustmentCombinations.Manufacturing_ManufacturingSourceMixAndEmissionFactors:
          return values.selectedEntities.find(
            (entity) =>
              // eslint-disable-next-line eqeqeq
              entity.id ==
              (accordion as EnergyTypeEmissionFactor).energy_type_id,
          )?.value;
        case MacroAdjustmentCombinations.Logistics_LogisticsRoadVehicleMixAndEmissionFactors:
        case MacroAdjustmentCombinations.Logistics_LogisticsOtherVehicleMixAndEmissionFactors:
          return values.selectedEntities.find(
            (entity) =>
              // eslint-disable-next-line eqeqeq
              entity.id ==
              (accordion as VehicleMixEmissionFactor).transport_mode_id,
          )?.value;
        case MacroAdjustmentCombinations.SalesMix_PackMix:
        case MacroAdjustmentCombinations.SalesMix_DLZShare:
          return (
            categories?.find(
              (category) =>
                // eslint-disable-next-line eqeqeq
                category.id == (accordion as CategoryPackMix).category_id,
            )?.category || ""
          );
        default:
          return convertIsocodeToCountry(
            countries,
            (accordion as CountryAccordionAggredatedDataType).country,
          );
      }
    },
    [adjustmentCombination, categories, countries, values.selectedEntities],
  );

  useEffect(() => {
    const expandedObject: { [key: string]: boolean } = {};

    aggregatedData?.forEach(
      (
        accordion:
          | CountryAccordionAggredatedDataType
          | EnergyTypeEmissionFactor
          | VehicleMixEmissionFactor
          | PartMaterialEmissionFactor
          | IngredientEmissionFactor
          | CategoryPackMix
          | CategoryDlzShare,
      ) => {
        switch (adjustmentCombination) {
          case MacroAdjustmentCombinations.Packaging_RecoveryRate:
          case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
          case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
          case MacroAdjustmentCombinations.CDE_CDEEnergyMixAndEmissionFactors:
          case MacroAdjustmentCombinations.CDE_CDEFleetBreakdown:
            if (
              !expandedObject[
                (accordion as CountryAccordionAggredatedDataType).country
              ]
            ) {
              expandedObject[
                (accordion as CountryAccordionAggredatedDataType).country
              ] = true;
            }
            break;
          case MacroAdjustmentCombinations.Packaging_PackagingEmissionFactors:
            if (
              !expandedObject[
                (accordion as PartMaterialEmissionFactor).part_material_id
              ]
            ) {
              expandedObject[
                (accordion as PartMaterialEmissionFactor).part_material_id
              ] = true;
            }
            break;
          case MacroAdjustmentCombinations.Ingredients_IngredientsEmissionFactors:
            if (
              !expandedObject[
                (accordion as IngredientEmissionFactor).ingredient_id
              ]
            ) {
              expandedObject[
                (accordion as IngredientEmissionFactor).ingredient_id
              ] = true;
            }
            break;
          case MacroAdjustmentCombinations.Manufacturing_ManufacturingSourceMixAndEmissionFactors:
            if (
              !expandedObject[
                (accordion as EnergyTypeEmissionFactor).energy_type_id
              ]
            ) {
              expandedObject[
                (accordion as EnergyTypeEmissionFactor).energy_type_id
              ] = true;
            }
            break;
          case MacroAdjustmentCombinations.Logistics_LogisticsOtherVehicleMixAndEmissionFactors:
          case MacroAdjustmentCombinations.Logistics_LogisticsRoadVehicleMixAndEmissionFactors:
            if (
              !expandedObject[
                (accordion as VehicleMixEmissionFactor).transport_mode_id
              ]
            ) {
              expandedObject[
                (accordion as VehicleMixEmissionFactor).transport_mode_id
              ] = true;
            }
            break;
          case MacroAdjustmentCombinations.SalesMix_PackMix:
          case MacroAdjustmentCombinations.SalesMix_DLZShare:
            if (!expandedObject[(accordion as CategoryPackMix).category_id]) {
              expandedObject[(accordion as CategoryPackMix).category_id] = true;
            }
            break;
          default:
            break;
        }
      },
    );

    setExpanded(expandedObject);
  }, [adjustmentCombination, aggregatedData]);

  const handleExpandedChange = useCallback((country: string) => {
    setExpanded((prev) => {
      const accordionsLength = Object.keys(prev).length;
      let expandedLength = 0;
      let collapsedLength = 0;

      Object.values(prev).forEach((value) =>
        value ? expandedLength++ : collapsedLength++,
      );

      //If this expand makes all accordions expanded switch to 'Collapse all' (Update isAllExpanded to be true)
      if (!prev[country] && expandedLength + 1 === accordionsLength) {
        setIsAllExpanded(true);
        //If this collapse makes all accordions collapsed switch to 'Expand all' (Update isAllExpanded to be false)
      } else if (prev[country] && collapsedLength + 1 === accordionsLength) {
        setIsAllExpanded(false);
      }

      return { ...prev, [country]: !prev[country] };
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setIsAllExpanded((prevIsAllExpanded) => {
      setExpanded((prevExpanded) => {
        const expandedObject: { [key: string]: boolean } =
          deepClone(prevExpanded);

        Object.keys(expandedObject).forEach((key) => {
          expandedObject[key] = !prevIsAllExpanded;
        });

        return expandedObject;
      });
      return !prevIsAllExpanded;
    });
  }, []);

  const renderTable = useCallback(
    (accordion: MacroAdjustmentTableAccordion) => {
      switch (adjustmentCombination) {
        case MacroAdjustmentCombinations.Packaging_RecoveryRate:
        case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
        case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
        case MacroAdjustmentCombinations.SalesMix_PackMix:
        case MacroAdjustmentCombinations.SalesMix_DLZShare:
          return (
            <MacroAdjustmentStepThreeBasicTable
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              base_year={baseYear}
              target_year={targetYear}
              adjustmentCombination={adjustmentCombination}
              aggregatedData={accordion as BasicTableAgreggatedDataType}
            />
          );

        default:
          return (
            <MacroAdjustmentStepThreeCompositeTable
              adjustmentCombination={adjustmentCombination}
              aggregatedData={accordion}
              baseYear={baseYear}
              targetYear={targetYear}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
            />
          );
      }
    },
    [
      adjustmentCombination,
      baseYear,
      setValidationErrors,
      targetYear,
      validationErrors,
    ],
  );

  const isAllNA = useCallback(
    (accordion: MacroAdjustmentTableAccordion): boolean => {
      switch (adjustmentCombination) {
        case MacroAdjustmentCombinations.SalesMix_PackMix:
          return (accordion as CategoryPackMix).target_year.every(
            (agg_value) => agg_value.pack_mix === 0,
          );

        case MacroAdjustmentCombinations.SalesMix_DLZShare:
          return (accordion as CategoryDlzShare).target_year.every(
            (agg_value) =>
              agg_value.dlz_share === 0 || agg_value.dlz_share === 100,
          );

        default:
          return false;
      }
    },
    [adjustmentCombination],
  );

  const showNAMessage = useMemo<boolean>(
    () =>
      aggregatedData?.some((accordion: MacroAdjustmentTableAccordion) =>
        isAllNA(accordion),
      ) || false,
    [aggregatedData, isAllNA],
  );

  return (
    <>
      <Grid container justifyContent="flex-end">
        <Grid>
          <Button variant="text" onClick={handleExpandAll}>
            {isAllExpanded
              ? t("macro:adjustmentsPage.wizard.stepThree.collapseAll")
              : t("macro:adjustmentsPage.wizard.stepThree.expandAll")}
          </Button>
        </Grid>
      </Grid>

      {showNAMessage && (
        <Alert
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
          aria-live="assertive"
          aria-atomic="true"
        >
          {t("macro:adjustmentsPage.wizard.stepThree.allNAMessage")}{" "}
        </Alert>
      )}

      {aggregatedData?.map((accordion: MacroAdjustmentTableAccordion) => {
        const hideAccordion = isAllNA(accordion);

        return !hideAccordion ? (
          <Accordion
            key={
              (accordion as CountryAccordionAggredatedDataType).country ||
              (accordion as PartMaterialEmissionFactor).part_material_id ||
              (accordion as IngredientEmissionFactor).ingredient_id ||
              (accordion as EnergyTypeEmissionFactor).energy_type_id ||
              (accordion as VehicleMixEmissionFactor).transport_mode_id ||
              (accordion as CategoryPackMix | CategoryDlzShare).category_id
            }
            expanded={
              expanded?.[
                (accordion as CountryAccordionAggredatedDataType).country ||
                  (accordion as PartMaterialEmissionFactor).part_material_id ||
                  (accordion as IngredientEmissionFactor).ingredient_id ||
                  (accordion as EnergyTypeEmissionFactor).energy_type_id ||
                  (accordion as VehicleMixEmissionFactor).transport_mode_id ||
                  (accordion as CategoryPackMix | CategoryDlzShare).category_id
              ] || false
            }
            onChange={() =>
              handleExpandedChange(
                ((accordion as CountryAccordionAggredatedDataType).country ||
                  (accordion as PartMaterialEmissionFactor).part_material_id ||
                  (accordion as IngredientEmissionFactor).ingredient_id ||
                  (accordion as EnergyTypeEmissionFactor).energy_type_id ||
                  (accordion as VehicleMixEmissionFactor).transport_mode_id ||
                  (accordion as CategoryPackMix | CategoryDlzShare)
                    .category_id) as string,
              )
            }
          >
            <AccordionSummary
              expandIcon={
                <CustomIcon
                  name={Icons.CHEVRON_DOWN}
                  width={20}
                  height={20}
                  fill={palette.primary.main}
                />
              }
            >
              <Typography variant={"h3"}>
                {getAccordionName(accordion)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>{renderTable(accordion)}</AccordionDetails>
          </Accordion>
        ) : null;
      })}
    </>
  );
}

export default MacroAdjustmentStepThreeAccordionArray;
